import { createFileRoute, Link } from "@tanstack/react-router";
import { frameworks } from "@/content/en/frameworks";
import { SectionHeading, BriefingCTA } from "@/components/en/EnPrimitives";
import { pageTitle } from "@/lib/brand";


const CANONICAL = "https://smartsustainability.ai/frameworks";
const TITLE = pageTitle("Frameworks", "en");
const DESCRIPTION =
  "Original models for reading a claim before accepting it — the platform's structured tools for AI, sustainability, and systems analysis.";

export const Route = createFileRoute("/frameworks/")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:url", content: CANONICAL },
      { property: "og:locale", content: "en_US" },
      { property: "og:locale:alternate", content: "tr_TR" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: TITLE },
      { name: "twitter:description", content: DESCRIPTION },
    ],
    links: [
      { rel: "canonical", href: CANONICAL },
      { rel: "alternate", hreflang: "en", href: CANONICAL },
      { rel: "alternate", hreflang: "x-default", href: CANONICAL },
    ],

    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "CollectionPage",
          name: TITLE,
          description: DESCRIPTION,
          url: CANONICAL,
          inLanguage: "en",
          hasPart: frameworks.map((f) => ({
            "@type": "CreativeWork",
            name: f.name,
            description: f.shortDescription,
            url: `${CANONICAL}/${f.slug}`,
          })),
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: "https://smartsustainability.ai" },
            { "@type": "ListItem", position: 2, name: "Frameworks", item: CANONICAL },
          ],
        }),
      },
    ],
  }),
  component: FrameworksIndex,
});


function FrameworksIndex() {
  return (
    <div className="mx-auto max-w-6xl px-6 py-16 md:py-20">
      <SectionHeading as="h1" kicker="Original models" title="Frameworks">
        Structured ways of reading a claim before accepting it. Each framework
        starts from a single question and offers a discipline for answering it.
      </SectionHeading>

      <div className="grid gap-6 md:grid-cols-2">
        {frameworks.map((f) => (
          <Link
            key={f.slug}
            to="/frameworks/$slug"
            params={{ slug: f.slug }}
            className="group rounded-md border border-ink/10 bg-paper p-6 transition-colors hover:border-ink/40"
          >
            <p className="text-[10px] uppercase tracking-[0.22em] text-clay">Framework</p>
            <h2 className="mt-3 font-serif text-xl text-ink group-hover:text-clay transition-colors">
              {f.name}
            </h2>
            <p className="mt-3 font-serif italic text-[15px] text-clay/90 leading-snug">
              {f.coreQuestion}
            </p>
            <p className="mt-3 text-[14px] leading-relaxed text-foreground/80">
              {f.shortDescription}
            </p>
          </Link>
        ))}
      </div>

      <BriefingCTA />
    </div>
  );
}
